import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Plus, Search, Phone, Mail, Building2, ArrowRight, Package } from 'lucide-react';
import ErpShell from '../components/ErpShell';
import { ErpPageHeader, ErpEmpty } from '../components/ErpPageHeader';
import { WhatsAppReminder } from '../components/WhatsAppReminder';
import { api } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

export default function ErpCustomers() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', company: '', idNumber: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, b] = await Promise.all([
        api.get('/customers'),
        api.get('/balances/summary').catch(() => ({ data: { balances: {} } })),
      ]);
      setCustomers(r.data.customers || []);
      setBalances(b.data.balances || {});
    } catch (e) {
      toast({ title: 'Failed to load customers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return customers;
    const s = q.toLowerCase();
    return customers.filter((c) =>
      (c.name || '').toLowerCase().includes(s) ||
      (c.company || '').toLowerCase().includes(s) ||
      (c.phone || '').toLowerCase().includes(s) ||
      (c.email || '').toLowerCase().includes(s) ||
      (c.idNumber || '').toLowerCase().includes(s)
    );
  }, [q, customers]);

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/customers', form);
      toast({ title: 'Customer created' });
      setShowForm(false);
      setForm({ name: '', phone: '', email: '', company: '', idNumber: '', address: '', notes: '' });
      load();
    } catch (e) {
      toast({ title: 'Failed to create', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ErpShell title="Customers">
      <ErpPageHeader
        eyebrow="Company Book"
        title="Customers"
        subtitle="Every contact, shipment, invoice and payment in one place."
        icon={Users}
        actions={
          <>
            <div className="relative">
              <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--gg-text-dim)' }} />
              <input
                data-testid="customer-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, phone…"
                className="gg-input ps-9"
                style={{ width: 260 }}
              />
            </div>
            <button onClick={() => setShowForm(true)} className="gg-btn-primary" data-testid="new-customer-btn">
              <Plus size={14} /> New Customer
            </button>
          </>
        }
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map((i) => (
            <div key={i} className="gg-card p-6 animate-pulse" style={{ height: 160 }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <ErpEmpty
          icon={Users}
          title={q ? 'No customers match your search' : 'No customers yet'}
          subtitle="Add your first customer to start tracking shipments, invoices and payments."
          action={<button onClick={() => setShowForm(true)} className="gg-btn-primary"><Plus size={14} /> Add Customer</button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="customers-grid">
          {filtered.map((c, idx) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.4) }}
              whileHover={{ y: -3 }}
              onClick={() => navigate(`/erp/customers/${c.id}`)}
              className="text-start gg-card gg-card-hover p-5"
              data-testid={`customer-card-${c.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 text-sm font-bold"
                    style={{
                      background: 'linear-gradient(135deg, var(--gg-accent), #E2C15B)',
                      color: '#fff',
                      boxShadow: 'var(--gg-accent-glow)',
                    }}
                  >
                    {(c.name || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate" style={{ color: 'var(--gg-text)' }}>{c.name}</p>
                    <p className="text-[10px] gg-tabular" style={{ color: 'var(--gg-accent)', letterSpacing: '0.05em' }}>{c.customerNumber ? `#${c.customerNumber}` : ''}</p>
                    {c.company && (
                      <p className="text-xs truncate flex items-center gap-1" style={{ color: 'var(--gg-text-dim)' }}>
                        <Building2 size={11} /> {c.company}
                      </p>
                    )}
                  </div>
                </div>
                <span className="gg-chip gg-chip-accent gg-tabular" style={{ padding: '3px 10px' }}>
                  <Package size={11} /> {c.shipmentCount || 0}
                </span>
              </div>
              <div className="space-y-1.5 mb-3">
                {(() => {
                  const bal = balances[c.id];
                  if (bal && bal.remaining > 0) {
                    return (
                      <span className="inline-flex items-center gap-2">
                        <span className="gg-chip gg-chip-danger gg-tabular" data-testid={`balance-chip-${c.id}`}>
                          ₪{bal.remaining.toLocaleString()} due
                        </span>
                        <WhatsAppReminder name={c.name} phone={c.phone} remaining={bal.remaining} compact />
                      </span>
                    );
                  }
                  if (bal && bal.charged > 0) {
                    return <span className="gg-chip gg-chip-emerald" data-testid={`balance-chip-${c.id}`}>Settled</span>;
                  }
                  return null;
                })()}
                {c.phone && (
                  <p className="text-xs flex items-center gap-2 truncate" style={{ color: 'var(--gg-text-secondary)' }}>
                    <Phone size={11} /> {c.phone}
                  </p>
                )}
                {c.email && (
                  <p className="text-xs flex items-center gap-2 truncate" style={{ color: 'var(--gg-text-secondary)' }}>
                    <Mail size={11} /> {c.email}
                  </p>
                )}
              </div>
              <p className="text-xs font-semibold flex items-center gap-1 mt-3" style={{ color: 'var(--gg-accent)' }}>
                Open profile <ArrowRight size={11} />
              </p>
            </motion.button>
          ))}
        </div>
      )}

      {/* New customer sheet */}
      {showForm && (
        <div className="gg-cmdk-overlay flex items-start justify-center pt-24" onClick={() => !saving && setShowForm(false)}>
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="gg-cmdk-panel p-6"
            data-testid="new-customer-form"
          >
            <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>New</p>
            <h2 className="gg-hero-title" style={{ fontSize: 24 }}>Add Customer</h2>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {[
                { k: 'name', label: 'Name *', span: 2 },
                { k: 'company', label: 'Company' },
                { k: 'idNumber', label: 'ID Number' },
                { k: 'phone', label: 'Phone' },
                { k: 'email', label: 'Email' },
                { k: 'address', label: 'Address', span: 2 },
                { k: 'notes', label: 'Notes', span: 2 },
              ].map((f) => (
                <div key={f.k} style={{ gridColumn: `span ${f.span || 1}` }}>
                  <label className="gg-eyebrow block mb-1">{f.label}</label>
                  <input
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="gg-input"
                    data-testid={`new-customer-${f.k}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} disabled={saving} className="gg-btn-secondary" data-testid="new-customer-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="gg-btn-primary" data-testid="new-customer-save">
                {saving ? 'Saving…' : 'Save Customer'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </ErpShell>
  );
}
